import { onDocumentReady, readStorage, writeStorage } from "@theme/utilities";

export const IDENTIFIED_STORAGE_KEY = "cpop_identified_customer";
// Read-only legacy key (no longer set / not imported elsewhere). See isIdentifiedCustomer.
const ALIA_SIGNUP_STORAGE_KEY = "alia_submitted";
export const ABRA_ACTIVE_PROMOTION_KEY = "abra:active-promotion";
// Theme-owned mirror of the active returning-customer promo. Abra removes `abra:active-promotion`
// during its own boot (then re-adds it), which clobbers our pre-paint state; this key is ours, so
// the pre-paint and runtime can resolve "active" through Abra's removal window and on return visits.
export const MIRROR_STORAGE_KEY = "theme:active-promotion-abra";
// How long we trust the mirror without a fresh Abra confirmation. Bounds stale "unlocked" if a promo
// is deactivated before its endsAt (Abra stops confirming → mirror ages out). Tunable.
const MIRROR_TTL_MS = 24 * 60 * 60 * 1000;
const ABRA_READY_MAX_RETRIES = 100;
const ABRA_READY_RETRY_MS = 100;
const CONFIG_ELEMENT_ID = "returning-customer-promo-config";

/** @type {boolean} */
let returningCustomerPromoActivationInFlight = false;

/**
 * @typedef {Object} ReturningCustomerPromoConfig
 * @property {boolean} enabled
 * @property {string} abralinkParam
 * @property {boolean} shopifyCustomerLoggedIn
 */

/**
 * @returns {ReturningCustomerPromoConfig | null}
 */
export function getReturningCustomerPromoConfig() {
  const element = document.getElementById(CONFIG_ELEMENT_ID);
  if (!element?.textContent) {
    return null;
  }

  try {
    return JSON.parse(element.textContent);
  } catch {
    return null;
  }
}

/**
 * Marks the visitor as an identified customer (idempotent).
 */
export function markIdentifiedCustomer() {
  writeStorage(IDENTIFIED_STORAGE_KEY, "true");
  window.Theme.customer.identified = true;
}

/**
 * @returns {boolean}
 */
export function isIdentifiedCustomer() {
  return (
    readStorage(IDENTIFIED_STORAGE_KEY) === "true" ||
    // Backward-compat only. `alia_submitted` (added ~2026-03-06) was set on every Alia
    // popup submit so Alia could jump returning submitters to the discount step via
    // embedded `$alia.popup.step()` code. The theme no longer SETS it
    // (cpop_identified_customer is canonical and covers all capture paths); we still READ
    // it so visitors flagged before that change stay recognized. Safe to remove once that
    // short cohort has aged out.
    readStorage(ALIA_SIGNUP_STORAGE_KEY) === "true"
  );
}

/**
 * Exposes identification on `window.Theme.customer.identified` for other scripts and tools.
 */
export function syncCustomerIdentifiedWindowFlag() {
  window.Theme.customer.identified = isIdentifiedCustomer();
}

/**
 * @returns {{ id: string, title?: string, discountCodes?: string[], endsAt?: string } | null}
 */
export function getActiveAbraPromotion() {
  try {
    const raw = readStorage(ABRA_ACTIVE_PROMOTION_KEY);
    if (!raw) {
      return null;
    }

    const data = JSON.parse(raw);
    // Ignore Abra's `expiresAt` — it's a ~30-min cache-refresh timer, NOT the promo's lifetime.
    // A stale cache can still be an active promo; `endsAt` (below) is the real end. Abra's payload
    // also has no `status` field, so presence + unexpired `endsAt` is what "active" means.
    if (
      data.promotion?.endsAt &&
      Date.now() > Date.parse(data.promotion.endsAt)
    ) {
      return null;
    }

    return data.promotion ?? null;
  } catch {
    return null;
  }
}

/**
 * Mirrors the active returning-customer promo into a theme-owned key Abra never clears, so the
 * pre-paint script and runtime resolve "active" even while Abra has temporarily removed
 * `abra:active-promotion` during boot, and on the next page load before Abra re-activates.
 * Call only when a REAL Abra signal confirms active, so `mirroredAt` tracks genuine confirmations.
 * @param {string} slug
 * @param {{ endsAt?: string } | null} [promotion]
 */
export function mirrorActivePromotion(slug, promotion) {
  if (!slug) return;

  let endsAt = promotion?.endsAt ?? null;
  if (endsAt == null) {
    // Preserve a previously-stored endsAt when this confirmation lacks one — e.g. a body-class-only
    // confirmation during Abra's removal window, where getActiveAbraPromotion() is momentarily null.
    try {
      const previous = JSON.parse(readStorage(MIRROR_STORAGE_KEY) || "null");
      if (previous && previous.slug === slug && previous.endsAt) {
        endsAt = previous.endsAt;
      }
    } catch {
      /* ignore — fall back to null */
    }
  }

  writeStorage(
    MIRROR_STORAGE_KEY,
    JSON.stringify({ slug, endsAt, mirroredAt: Date.now() }),
  );
}

/**
 * @param {string} slug
 * @returns {boolean} True if the theme-owned mirror confirms the slug is active — matching slug,
 *   before `endsAt`, and refreshed within the TTL (so a deactivated promo ages out).
 */
export function hasMirroredPromotion(slug) {
  if (!slug) return false;
  try {
    const raw = readStorage(MIRROR_STORAGE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (data.slug !== slug) return false;
    if (data.endsAt && Date.now() > Date.parse(data.endsAt)) return false;
    if (!data.mirroredAt || Date.now() > data.mirroredAt + MIRROR_TTL_MS) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * @returns {boolean}
 */
function isEmailUtmMediumClick() {
  return (
    new URLSearchParams(window.location.search)
      .get("utm_medium")
      ?.trim()
      .toLowerCase() === "email"
  );
}

/**
 * Marks visitors arriving from email campaigns (utm_medium=email).
 */
function detectEmailUtmMediumOnPage() {
  if (isEmailUtmMediumClick()) {
    markIdentifiedCustomer();
  }
}

/**
 * @param {string} abralinkParam
 * @returns {boolean}
 */
function hasAbraLinkParamOnPage(abralinkParam) {
  if (!abralinkParam) {
    return false;
  }

  return (
    new URLSearchParams(window.location.search).get("abralink") ===
    abralinkParam
  );
}

/**
 * @param {(abra: typeof window.Abra | null) => void} callback
 * @param {number} [retryCount]
 */
function onAbraReady(callback, retryCount = 0) {
  if (window.Abra?.initialized) {
    callback(window.Abra);
    return;
  }

  if (retryCount <= ABRA_READY_MAX_RETRIES) {
    setTimeout(
      () => onAbraReady(callback, retryCount + 1),
      ABRA_READY_RETRY_MS,
    );
    return;
  }

  callback(null);
}

/**
 * Activates the returning-customer Abra promotion via `window.Abra.activate`
 * (same path as the `abralink` query param, without a page reload).
 *
 * @param {ReturningCustomerPromoConfig} config
 * @returns {boolean} True if activation was scheduled.
 */
export function tryActivateReturningCustomerPromo(config) {
  if (!config.enabled || !config.abralinkParam) {
    return false;
  }

  if (!isIdentifiedCustomer()) {
    return false;
  }

  if (hasAbraLinkParamOnPage(config.abralinkParam)) {
    return false;
  }

  if (returningCustomerPromoActivationInFlight) {
    return false;
  }

  returningCustomerPromoActivationInFlight = true;

  onAbraReady(async (abra) => {
    try {
      if (!abra) {
        return;
      }

      await abra.activate(config.abralinkParam, undefined);
    } finally {
      returningCustomerPromoActivationInFlight = false;
    }
  });

  return true;
}

function detectEmailSignupSuccessOnPage() {
  if (document.querySelector(".email-signup__message--success")) {
    markIdentifiedCustomer();
  }
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isExcludedEmailCaptureForm(form) {
  const action = (form.getAttribute("action") || "").toLowerCase();

  if (
    action.includes("/account/login") ||
    action.includes("/account/recover") ||
    action.includes("/challenge")
  ) {
    return true;
  }

  if (form.querySelector('input[type="password"]')) {
    return true;
  }

  if (
    form.closest("contact-form") ||
    form.classList.contains("contact-form__form")
  ) {
    return true;
  }

  if (form.querySelector('textarea[name="contact[body]"]')) {
    return true;
  }

  if (form.querySelector('input[name="comment[email]"]')) {
    return true;
  }

  return false;
}

/**
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement | null}
 */
function getValidEmailInputFromForm(form) {
  const emailInput = form.querySelector('input[name="email"]');
  if (!(emailInput instanceof HTMLInputElement)) {
    return null;
  }

  const value = emailInput.value.trim();
  if (!value || !emailInput.checkValidity()) {
    return null;
  }

  return emailInput;
}

/**
 * @param {HTMLFormElement} form
 */
function handleEmailFormSubmit(form) {
  if (isIdentifiedCustomer() || isExcludedEmailCaptureForm(form)) {
    return;
  }

  if (!getValidEmailInputFromForm(form)) {
    return;
  }

  markIdentifiedCustomer();
  syncCustomerIdentifiedWindowFlag();
  activateReturningCustomerPromoIfEligible();
}

/**
 * @param {SubmitEvent} event
 */
function onGlobalEmailFormSubmit(event) {
  if (!(event.target instanceof HTMLFormElement)) {
    return;
  }

  handleEmailFormSubmit(event.target);
}

/**
 * Listens for any email form submit (capture) and email+button clicks in embedded widgets.
 */
export function setupGlobalEmailSubmissionListeners() {
  document.addEventListener("submit", onGlobalEmailFormSubmit, true);
}

/**
 * Attempts Abra promo activation for an already-identified visitor (e.g. after Alia signup).
 */
export function activateReturningCustomerPromoIfEligible() {
  const config = getReturningCustomerPromoConfig();
  if (!config?.enabled || !isIdentifiedCustomer()) {
    return;
  }

  tryActivateReturningCustomerPromo(config);
}

/**
 * Marks the visitor identified and activates the returning-customer promo when enabled.
 */
export function onCustomerIdentified() {
  markIdentifiedCustomer();
  activateReturningCustomerPromoIfEligible();
}

/**
 * @param {ReturningCustomerPromoConfig} config
 */
function runReturningCustomerPromoFlow(config) {
  if (config.shopifyCustomerLoggedIn) {
    markIdentifiedCustomer();
  } else {
    detectEmailSignupSuccessOnPage();
  }

  activateReturningCustomerPromoIfEligible();
}

onDocumentReady(() => {
  // This module loads on every page (custom-event-handlers.js imports it), so stay fully
  // inert unless the feature is enabled — otherwise email-submit identification + the
  // window-flag sync would run with the feature off.
  const config = getReturningCustomerPromoConfig();
  if (!config?.enabled) {
    return;
  }

  setupGlobalEmailSubmissionListeners();
  syncCustomerIdentifiedWindowFlag();
  detectEmailUtmMediumOnPage();
  runReturningCustomerPromoFlow(config);
});
